<template>
  <Spinner
    v-if="isLoading"
    color="primary"
    size="5rem"
    width="0.5rem"
    class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
  />

  <div v-else>
    <!--
      M7, Tela 4 (balão ⑰): a exigência de check mora na política de registro de plantão.
      Aqui só o resumo do que ela decidiu — SEM interruptores, para não recriar a flag
      escondida da escala que deixou o incidente sem explicação.
    -->
    <div
      class="mb-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-left"
      data-testid="schedule-requirements-registration-block"
    >
      <h4 class="flex flex-wrap items-center gap-2 text-sm font-semibold text-textPrimary">
        <SVGIcon :path="mdiLock" class="w-4 h-4 shrink-0 text-gray-500" />
        <span>Registro de plantão: gerenciado pela política de registro</span>
        <span
          class="rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-normal text-gray-600"
        >
          somente leitura
        </span>
      </h4>

      <p class="mt-1.5 text-xs text-gray-600">
        Exigência de check, público (nosso time / externos) e método de check são definidos na
        política de registro de plantão vigente desta escala.
      </p>

      <p
        class="mt-1.5 text-xs text-gray-600"
        data-testid="schedule-requirements-registration-summary"
      >
        <strong>Hoje:</strong> {{ registrationSummary }}
      </p>

      <button
        type="button"
        class="mt-2.5 text-xs font-semibold text-primary30 hover:underline"
        data-testid="schedule-requirements-registration-link"
        @click="emit('open-policy-tab')"
      >
        Ver política
      </button>
    </div>

    <EditableItemGroup
      title="Posições"
      itemLabel="Nova posição"
      itemActiveLabel="Ativar posição"
      :createButtonLabel="'Criar Posição'"
      :type="'positions'"
      :items="activePositions.map((p) => p.name)"
      :inactiveItems="inactivePositions.map((p) => p.name)"
      @update="(i, val) => onRename('positions', i, val)"
      @deactivate="(i) => onToggle('positions', i, false)"
      @reactivate="(i) => onToggle('positions', i, true)"
      @create="(newName) => onCreate('positions', newName)"
    />

    <hr class="border-t border-gray-300 my-2 px-2" />

    <EditableItemGroup
      title="Turnos"
      itemLabel="Novo turno"
      itemActiveLabel="Ativar turno"
      :createButtonLabel="'Criar Turno'"
      :type="'shifts'"
      :items="activeShifts.map((s) => s.name)"
      :inactiveItems="inactiveShifts.map((s) => s.name)"
      @update="(i, val) => onRename('shifts', i, val)"
      @deactivate="(i) => onToggle('shifts', i, false)"
      @reactivate="(i) => onToggle('shifts', i, true)"
      @create="(newName) => onCreate('shifts', newName)"
    />

    <hr class="border-t border-gray-300 my-2 px-2" />

    <EditableLevelGroup
      title="Níveis de Formação"
      itemLabel="Nova formação"
      :createButtonLabel="'Criar Formação'"
      :type="'formations'"
      :items="activeFormations.map((f) => f.name)"
      :inactiveItems="inactiveFormations.map((f) => f.name)"
      :requiresDoctorPaymentFormation="requiresDoctorPaymentFormationRef"
      @update="(i, val) => onRename('formations', i, val)"
      @deactivate="(i) => onToggle('formations', i, false)"
      @reactivate="(i) => onToggle('formations', i, true)"
      @create="(newName) => onCreate('formations', newName)"
      @updateRequiresDoctorPaymentFormation="onUpdateRequiresDoctorPaymentFormation"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { mdiLock } from '@mdi/js'
import SVGIcon from '@/components/SVGIcon.vue'
import EditableItemGroup from '../../../menu/EditableItemGroup.vue'
import EditableLevelGroup from '../../../menu/EditableLevelGroup.vue'
import { PositionResponse } from '@/Network/Types/Responses/PositionResponses'
import { ShiftResponse } from '@/Network/Types/Responses/ShiftResponses'
import { FormationResponse } from '@/Network/Types/Responses/FormationResponses'
import Network from '@/Network'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import Spinner from '@/components/others/Spinner.vue'
import type { DSAttendancePolicyData } from '@/types/ds-attendance.types'
import {
  resolveCurrentCheckRequirement,
  buildScheduleCheckSummary,
  type ScheduleCheckFlags
} from '@/utils/registrationCheck'

const toast = useToast()
const { notifyError } = useErrorToast()

const emit = defineEmits<{
  (e: 'update:isSaving', value: boolean): void
  (e: 'open-policy-tab'): void
}>()

const props = defineProps<{
  scheduleId: number
}>()

type Key = 'positions' | 'shifts' | 'formations'

const activePositions = ref<PositionResponse[]>([])
const inactivePositions = ref<PositionResponse[]>([])
const activeShifts = ref<ShiftResponse[]>([])
const inactiveShifts = ref<ShiftResponse[]>([])
const activeFormations = ref<FormationResponse[]>([])
const inactiveFormations = ref<FormationResponse[]>([])
const requiresDoctorPaymentFormationRef = ref<boolean>(false)
const isLoading = ref(true)
const hasUnsavedChanges = ref(false)

/** Insumos do bloco somente-leitura do registro de plantão (M7, Tela 4). */
const registrationPolicies = ref<DSAttendancePolicyData[]>([])
const scheduleCheckFlags = ref<ScheduleCheckFlags | null>(null)

/** `política ?? escala`: a flag legada só responde enquanto não há política vigente. */
const currentCheckRequirement = computed(() =>
  resolveCurrentCheckRequirement(registrationPolicies.value, scheduleCheckFlags.value)
)
const registrationSummary = computed(() => buildScheduleCheckSummary(currentCheckRequirement.value))

/**
 * Leitura TOLERANTE A FALHA: o bloco somente-leitura é informativo e não pode derrubar
 * a aba de Requisitos nem disparar toast. Sem políticas, o resumo cai nas flags da escala.
 */
const loadRegistrationPolicies = async () => {
  try {
    registrationPolicies.value = await Network.dsAttendance.listPolicies(props.scheduleId)
  } catch {
    registrationPolicies.value = []
  }
}

const loadRequirements = async () => {
  isLoading.value = true
  const registrationPoliciesLoad = loadRegistrationPolicies()
  try {
    const [positions, shifts, formations, schedule] = await Promise.all([
      Network.position.getByScheduleId(props.scheduleId),
      Network.shift.getByScheduleId(props.scheduleId),
      Network.paymentFormation.getByScheduleId(props.scheduleId),
      Network.schedule.getById(props.scheduleId)
    ])

    activePositions.value = positions.data.filter((p) => p.isActive)
    inactivePositions.value = positions.data.filter((p) => !p.isActive)
    activeShifts.value = shifts.data.filter((s) => s.isActive)
    inactiveShifts.value = shifts.data.filter((s) => !s.isActive)
    activeFormations.value = formations.data.filter((f) => f.isActive)
    inactiveFormations.value = formations.data.filter((f) => !f.isActive)
    requiresDoctorPaymentFormationRef.value = schedule.requiresDoctorPaymentFormation
    scheduleCheckFlags.value = {
      ourTeam: Boolean(schedule.requiredCheckOurTeam),
      notOurTeam: Boolean(schedule.requiredCheckNotOurTeam)
    }
  } catch (err) {
    notifyError(err, 'Não foi possível carregar os requisitos da escala.')
  } finally {
    await registrationPoliciesLoad
    isLoading.value = false
  }
}

onMounted(() => {
  loadRequirements()
})

function onToggle(key: Key, index: number, makeActive: boolean) {
  const src = makeActive ? inactiveMap[key] : activeMap[key]
  const dst = makeActive ? activeMap[key] : inactiveMap[key]
  const item = src.value.splice(index, 1)[0]
  dst.value.push({ ...item, isActive: makeActive })
  hasUnsavedChanges.value = true
}

function onUpdateRequiresDoctorPaymentFormation(value: boolean) {
  requiresDoctorPaymentFormationRef.value = value
  hasUnsavedChanges.value = true
}

function onRename(key: Key, index: number, newName: string) {
  const item = mapByKey(key)[index]
  if (item.name === newName) return

  item.name = newName.trim()
  hasUnsavedChanges.value = true
}

function onCreate(key: Key, newName: string) {
  const newItem = { name: newName, isActive: true }
  activeMap[key].value.push(newItem as any)
  hasUnsavedChanges.value = true
}

const activeMap: Record<Key, any> = {
  positions: activePositions,
  shifts: activeShifts,
  formations: activeFormations
}

const inactiveMap: Record<Key, any> = {
  positions: inactivePositions,
  shifts: inactiveShifts,
  formations: inactiveFormations
}

function mapByKey(key: Key) {
  return key === 'positions'
    ? activePositions.value.concat(inactivePositions.value)
    : key === 'shifts'
      ? activeShifts.value.concat(inactiveShifts.value)
      : activeFormations.value.concat(inactiveFormations.value)
}

const handleSave = async () => {
  if (requiresDoctorPaymentFormationRef.value && activeFormations.value.length === 0) {
    toast.error(
      'Para salvar com a opção de formação habilitada, você deve ter ao menos uma formação ativa.'
    )
    return
  }

  emit('update:isSaving', true)

  try {
    await Promise.all([
      Network.position.updateBulk(props.scheduleId, [
        ...activePositions.value,
        ...inactivePositions.value
      ]),
      Network.shift.updateBulk(props.scheduleId, [...activeShifts.value, ...inactiveShifts.value]),
      Network.paymentFormation.updateBulk(props.scheduleId, [
        ...activeFormations.value,
        ...inactiveFormations.value
      ]),
      Network.schedule.update(props.scheduleId, {
        requiresDoctorPaymentFormation: requiresDoctorPaymentFormationRef.value
      })
    ])

    hasUnsavedChanges.value = false
    toast.success('Requisitos salvos com sucesso.')
  } catch (e: unknown) {
    // Os `updateBulk` de posição/turno/formação e o `schedule.update` normalizam
    // o erro em ServiceError, então `axios.isAxiosError` deixou de casar e a
    // mensagem do backend se perdia no fallback fixo.
    notifyError(e, 'Não foi possível salvar os requisitos da escala.')
  } finally {
    emit('update:isSaving', false)
  }
}

defineExpose({
  handleSave,
  hasUnsavedChanges
})
</script>
